import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  createStockCorrection,
  getStockCorrection,
  listStockCorrections,
} from './StockCorrections.service';

import {
  CreateStockCorrectionRequest,
  CreateStockCorrectionResult,
  StockCorrection,
  StockCorrectionDetail,
  StockCorrectionsQuery,
} from './StockCorrections.types';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import notificationError from '@/utils/functions/notificationError';
import { stockCorrectionsDicionaryError } from './StockCorrections.errors';
import { PageResultStock } from '@/core/request/PageResultStock';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useStockCorrections(
  params?: StockCorrectionsQuery,
): UseInfiniteQueryResult<PageResultStock<StockCorrection>> {
  const query = useInfiniteQuery<PageResultStock<StockCorrection>>(
    ['stockCorrections', params],
    ({ pageParam }) => {
      const req: StockCorrectionsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listStockCorrections(req);
    },
    {
      getNextPageParam: (lastPage: PageResultStock<StockCorrection>) =>
        lastPage.result.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useStockCorrection(
  id: number,
): UseQueryResult<StockCorrectionDetail> {
  const query = useQuery<StockCorrectionDetail>(['stockCorrections', id], () =>
    getStockCorrection(id),
  );

  useServiceError(query.error);

  return query;
}

export function useCreateStockCorrection(): UseMutationResult<
  CreateStockCorrectionResult,
  unknown,
  CreateStockCorrectionRequest,
  unknown
> {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (req: CreateStockCorrectionRequest) => createStockCorrection(req),
    {
      onSuccess: () => {
        queryChanel({
          invalidateQueries: [['partStockPrices']],
        });
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => stockCorrectionsDicionaryError[err] || err),
        });
      },
    },
  );

  return mutation;
}
